import { useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function FeedbackForm({ services }) {
  const [feedbackForm, setFeedbackForm] = useState({
    client_name: '',
    client_email: '',
    rating: 5,
    message: '',
    massage_type: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackForm.client_name || !feedbackForm.message) {
      toast.error('Please fill in your name and feedback');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(feedbackForm)
      });
      if (!response.ok) throw new Error('Failed');
      toast.success('Thank you for your feedback!');
      setFeedbackForm({ client_name: '', client_email: '', rating: 5, message: '', massage_type: '' });
    } catch {
      toast.error('Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 border border-healing-border">
      <h3 className="font-heading text-2xl text-healing-burgundy mb-6 text-center">Share Your Experience</h3>
      <form onSubmit={handleFeedbackSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="feedback_name" className="text-healing-burgundy">Your Name *</Label>
            <Input
              id="feedback_name"
              value={feedbackForm.client_name}
              onChange={(e) => setFeedbackForm(prev => ({ ...prev, client_name: e.target.value }))}
              placeholder="Your name"
              className="border-healing-border"
              data-testid="feedback-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="feedback_email" className="text-healing-burgundy">Email (Optional)</Label>
            <Input
              id="feedback_email"
              type="email"
              value={feedbackForm.client_email}
              onChange={(e) => setFeedbackForm(prev => ({ ...prev, client_email: e.target.value }))}
              placeholder="your@email.com"
              className="border-healing-border"
              data-testid="feedback-email-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-healing-burgundy">Rating *</Label>
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setFeedbackForm(prev => ({ ...prev, rating: star }))}
                className="focus:outline-none"
                data-testid={`rating-star-${star}`}
              >
                <Star 
                  className={`h-8 w-8 transition-colors ${
                    star <= feedbackForm.rating 
                      ? 'text-healing-purple fill-current' 
                      : 'text-healing-border'
                  }`} 
                />
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="feedback_message" className="text-healing-burgundy">Your Feedback *</Label>
          <Textarea
            id="feedback_message"
            value={feedbackForm.message}
            onChange={(e) => setFeedbackForm(prev => ({ ...prev, message: e.target.value }))}
            placeholder="Tell us about your experience..."
            className="border-healing-border min-h-[120px]"
            data-testid="feedback-message-input"
          />
        </div>

        {services.length > 0 && (
          <div className="space-y-2">
            <Label className="text-healing-burgundy">Service Received (Optional)</Label>
            <Select 
              value={feedbackForm.massage_type} 
              onValueChange={(v) => setFeedbackForm(prev => ({ ...prev, massage_type: v }))}
            >
              <SelectTrigger className="border-healing-border" data-testid="feedback-service-select">
                <SelectValue placeholder="Select service" />
              </SelectTrigger>
              <SelectContent>
                {services.map(s => (
                  <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Button 
          type="submit" 
          disabled={submitting}
          className="w-full bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full"
          data-testid="submit-feedback-btn"
        >
          {submitting ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </form>
    </div>
  );
}
